import { Badge } from '../ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import {
  HelpCircle,
  Phone,
  Zap,
  Flame,
  Crown,
  Clock,
  MessageCircleReply,
  type LucideIcon,
} from 'lucide-react'
import {
  formatEnumName,
  getInquiryStageColor,
  getInquiryTagColor,
  getInquiryTagLabel,
  getBookingStageColor,
  getEventStageColor,
  getQuoteStatusColor,
  getQuoteStatusLabel,
  getPaymentStatusColor,
  getInvoiceStatusColor,
  getInvoiceStatusLabel,
  getItemUnitColor,
  getParentTypeColor,
  getDiscountTypeColor,
  getUserSchedulingStatusColor,
  getTaskStatusColor,
  getOrganizationRoleColor,
  getOrganizationFeatureColor,
  getPermissionColor,
  type BadgeVariant,
} from '@/lib/enum-utils'
import {
  InquiryStageEnum,
  InquiryTagEnum,
  BookingStageEnum,
  EventStageEnum,
  PaymentStatusEnum,
  ItemUnitEnum,
  ParentTypeEnum,
  DiscountTypeEnum,
  UserSchedulingStatusEnum,
  TaskStatusEnum,
  OrganizationRoleEnum,
  OrganizationFeatureEnum,
  PermissionEnum,
} from '@/gen/cortado/v1/settings_pb'
import { useEventTypes } from '@/hooks/use-event-types'

interface EnumBadgeProps {
  value: number
  enumType: any
  variant?: BadgeVariant
  className?: string
}

/**
 * Generic badge component for rendering any enum value with automatic formatting
 *
 * @example
 * <EnumBadge value={2} enumType={InquiryStageEnum} variant="warning" />
 */
export function EnumBadge({ value, enumType, variant, className }: EnumBadgeProps) {
  const displayName = formatEnumName(value, enumType)

  return (
    <Badge variant={variant} className={className}>
      {displayName}
    </Badge>
  )
}

// Specific badge components for each enum type with built-in color mapping
// Color logic is centralized in enum-utils.ts to maintain single source of truth

interface InquiryStageBadgeProps {
  stage: number
  className?: string
}

/**
 * Badge component specifically for InquiryStage enums
 * Automatically applies the correct color and formatting based on the stage value
 *
 * @example
 * <InquiryStageBadge stage={2} />
 */
export function InquiryStageBadge({ stage, className }: InquiryStageBadgeProps) {
  return <EnumBadge value={stage} enumType={InquiryStageEnum} variant={getInquiryStageColor(stage)} className={className} />
}

interface InquiryTagBadgeProps {
  tag: InquiryTagEnum
  className?: string
  compact?: boolean
}

/**
 * Returns a Lucide icon component for the given InquiryTag enum value
 */
export function getInquiryTagIcon(tag: InquiryTagEnum): LucideIcon {
  switch (tag) {
    case InquiryTagEnum.INQUIRY_TAG_FOLLOW_UP:
      return Phone
    case InquiryTagEnum.INQUIRY_TAG_URGENT:
      return Zap
    case InquiryTagEnum.INQUIRY_TAG_HOT_LEAD:
      return Flame
    case InquiryTagEnum.INQUIRY_TAG_VIP:
      return Crown
    case InquiryTagEnum.INQUIRY_TAG_WAITING:
      return Clock
    case InquiryTagEnum.INQUIRY_TAG_REPLY_NEEDED:
      return MessageCircleReply
    default:
      return HelpCircle
  }
}

/**
 * Badge component for InquiryTag enums
 * Use compact=true for icon-only display in tight spaces (like kanban cards)
 *
 * @example
 * <InquiryTagBadge tag={InquiryTagEnum.INQUIRY_TAG_FOLLOW_UP} />
 * <InquiryTagBadge tag={InquiryTagEnum.INQUIRY_TAG_URGENT} compact />
 */
export function InquiryTagBadge({ tag, className, compact = false }: InquiryTagBadgeProps) {
  const label = getInquiryTagLabel(tag)
  const Icon = getInquiryTagIcon(tag)

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Badge variant={getInquiryTagColor(tag)} className={`px-1.5 ${className || ''}`}>
              <Icon className="h-3 w-3" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top">
            {label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Badge variant={getInquiryTagColor(tag)} className={className}>
      {label}
    </Badge>
  )
}

/**
 * Display multiple inquiry tags as a row of badges
 * Shows nothing if tags array is empty
 */
interface InquiryTagListProps {
  tags: InquiryTagEnum[]
  className?: string
  compact?: boolean
}

export function InquiryTagList({ tags, className, compact = false }: InquiryTagListProps) {
  if (!tags || tags.length === 0) return null

  return (
    <div className={`flex flex-wrap gap-1 ${className || ''}`}>
      {tags.map((tag) => (
        <InquiryTagBadge key={tag} tag={tag} compact={compact} />
      ))}
    </div>
  )
}

/**
 * Legend showing all inquiry tag icons with their labels
 * Useful for displaying a key near kanban boards or list views
 */
interface InquiryTagLegendProps {
  className?: string
}

export function InquiryTagLegend({ className }: InquiryTagLegendProps) {
  const tags = [
    InquiryTagEnum.INQUIRY_TAG_FOLLOW_UP,
    InquiryTagEnum.INQUIRY_TAG_URGENT,
    InquiryTagEnum.INQUIRY_TAG_HOT_LEAD,
    InquiryTagEnum.INQUIRY_TAG_VIP,
    InquiryTagEnum.INQUIRY_TAG_WAITING,
    InquiryTagEnum.INQUIRY_TAG_REPLY_NEEDED,
  ]

  return (
    <div className={`flex items-center gap-3 text-xs text-muted-foreground ${className || ''}`}>
      {tags.map((tag) => {
        const Icon = getInquiryTagIcon(tag)
        const label = getInquiryTagLabel(tag)
        return (
          <div key={tag} className="flex items-center gap-1">
            <Badge variant={getInquiryTagColor(tag)} className="px-1.5">
              <Icon className="h-3 w-3" />
            </Badge>
            <span>{label}</span>
          </div>
        )
      })}
    </div>
  )
}

interface BookingStageBadgeProps {
  stage: number
  className?: string
}

export function BookingStageBadge({ stage, className }: BookingStageBadgeProps) {
  return <EnumBadge value={stage} enumType={BookingStageEnum} variant={getBookingStageColor(stage)} className={className} />
}

interface EventStageBadgeProps {
  stage: number
  className?: string
}

export function EventStageBadge({ stage, className }: EventStageBadgeProps) {
  return <EnumBadge value={stage} enumType={EventStageEnum} variant={getEventStageColor(stage)} className={className} />
}

interface QuoteStatusBadgeProps {
  status: number
  className?: string
  /** Replaces the app-wide status label for this badge only (e.g. "Sent" on job quote tiles). */
  label?: string
}

export function QuoteStatusBadge({ status, className, label }: QuoteStatusBadgeProps) {
  return (
    <Badge variant={getQuoteStatusColor(status)} className={className}>
      {label ?? getQuoteStatusLabel(status)}
    </Badge>
  )
}

interface PaymentStatusBadgeProps {
  status: number
  className?: string
}

export function PaymentStatusBadge({ status, className }: PaymentStatusBadgeProps) {
  return <EnumBadge value={status} enumType={PaymentStatusEnum} variant={getPaymentStatusColor(status)} className={className} />
}

interface InvoiceStatusBadgeProps {
  status: number
  className?: string
}

export function InvoiceStatusBadge({ status, className }: InvoiceStatusBadgeProps) {
  return (
    <Badge variant={getInvoiceStatusColor(status)} className={className}>
      {getInvoiceStatusLabel(status)}
    </Badge>
  )
}

interface TaskStatusBadgeProps {
  status: number
  className?: string
}

export function TaskStatusBadge({ status, className }: TaskStatusBadgeProps) {
  return <EnumBadge value={status} enumType={TaskStatusEnum} variant={getTaskStatusColor(status)} className={className} />
}

interface ItemUnitBadgeProps {
  unit: number
  className?: string
}

export function ItemUnitBadge({ unit, className }: ItemUnitBadgeProps) {
  return <EnumBadge value={unit} enumType={ItemUnitEnum} variant={getItemUnitColor(unit)} className={className} />
}

interface EventTypeBadgeProps {
  /** The organization event type key stored on the record (e.g. "Wedding"). */
  type: string | undefined
  className?: string
}

/**
 * Badge for an organization event type.
 *
 * Label and color come from the organization's configured event types, so
 * archived types still render correctly on historical records.
 */
export function EventTypeBadge({ type, className }: EventTypeBadgeProps) {
  const { getEventTypeLabel, getEventTypeColor } = useEventTypes()

  if (!type) return null

  const label = getEventTypeLabel(type)

  // Labels are org-configurable and can be arbitrarily long, so the badge caps
  // itself to whatever width its container allows and ellipsizes rather than
  // forcing the container wider. `max-w-full` is inert unless a parent
  // constrains the width (see the leads kanban card).
  return (
    <Badge variant={getEventTypeColor(type)} className={`max-w-full ${className || ''}`} title={label}>
      <span className="truncate">{label}</span>
    </Badge>
  )
}

interface EventTypeLabelProps {
  /** The organization event type key stored on the record (e.g. "Wedding"). */
  type: string | undefined
  className?: string
  /** Rendered when the record has no event type. */
  placeholder?: string
}

/**
 * Plain-text label for an organization event type.
 *
 * A component (rather than a bare helper) so table cell renderers can resolve
 * the org's labels without threading the hook through every column def.
 */
export function EventTypeLabel({ type, className, placeholder = '-' }: EventTypeLabelProps) {
  const { getEventTypeLabel } = useEventTypes()

  return <span className={className}>{type ? getEventTypeLabel(type) : placeholder}</span>
}

interface ParentTypeBadgeProps {
  type: number
  className?: string
}

export function ParentTypeBadge({ type, className }: ParentTypeBadgeProps) {
  return <EnumBadge value={type} enumType={ParentTypeEnum} variant={getParentTypeColor(type)} className={className} />
}

interface DiscountTypeBadgeProps {
  type: number
  className?: string
}

export function DiscountTypeBadge({ type, className }: DiscountTypeBadgeProps) {
  return <EnumBadge value={type} enumType={DiscountTypeEnum} variant={getDiscountTypeColor(type)} className={className} />
}

interface UserSchedulingStatusBadgeProps {
  status: number
  className?: string
}

export function UserSchedulingStatusBadge({ status, className }: UserSchedulingStatusBadgeProps) {
  return <EnumBadge value={status} enumType={UserSchedulingStatusEnum} variant={getUserSchedulingStatusColor(status)} className={className} />
}

interface OrganizationRoleBadgeProps {
  role: number
  className?: string
}

export function OrganizationRoleBadge({ role, className }: OrganizationRoleBadgeProps) {
  return <EnumBadge value={role} enumType={OrganizationRoleEnum} variant={getOrganizationRoleColor(role)} className={className} />
}

interface OrganizationFeatureBadgeProps {
  feature: number
  className?: string
}

export function OrganizationFeatureBadge({ feature, className }: OrganizationFeatureBadgeProps) {
  return <EnumBadge value={feature} enumType={OrganizationFeatureEnum} variant={getOrganizationFeatureColor(feature)} className={className} />
}

interface PermissionBadgeProps {
  permission: number
  className?: string
}

export function PermissionBadge({ permission, className }: PermissionBadgeProps) {
  return <EnumBadge value={permission} enumType={PermissionEnum} variant={getPermissionColor(permission)} className={className} />
}
